import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AllowanceMeterItem,
  ProductRuleViolation,
  ProductRuleViolationOption,
} from 'src/app/models/cart-models';
import { DutyfreeStorageService } from './dutyfree-storage.service';

export type AllowanceMeterState = 'empty' | 'ok' | 'near' | 'over';

export interface AllowanceMeterGroup {
  key: string;
  label?: string;
  hint?: string;
  items: AllowanceMeterItem[];
}

/**
 * Single source of truth for duty-free customs allowance guidance: builds the
 * meters from the server's `productRuleViolations`, scales them by traveller
 * count, and formats every label. Shared by the cart and the product page so
 * both read identically.
 */
@Injectable({ providedIn: 'root' })
export class DutyfreeAllowanceService {
  readonly maxTravellerCount = 9;

  constructor(
    private translate: TranslateService,
    private dutyfreeStorage: DutyfreeStorageService
  ) {}

  get travellerCount(): number {
    return this.dutyfreeStorage.getTravellerCount();
  }

  setTravellerCount(count: number): number {
    const safe = Math.min(this.maxTravellerCount, Math.max(1, count));
    this.dutyfreeStorage.setTravellerCount(safe);
    return safe;
  }

  travellerLabel(count = this.travellerCount): string {
    return this.translate.instant(
      count === 1
        ? 'dutyfree.retail_cart.allowance_adult'
        : 'dutyfree.retail_cart.allowance_adults',
      { count }
    );
  }

  normalizeViolations(
    violations: Record<string, ProductRuleViolation> | null | undefined
  ): Record<string, ProductRuleViolation> {
    if (!violations || typeof violations !== 'object') {
      return {};
    }
    return violations;
  }

  /**
   * Meter groups, with the categories a shopper has already filled floated to
   * the top (fullest first) so the relevant limits are visible without scrolling.
   */
  getMeterGroups(
    violations: Record<string, ProductRuleViolation>,
    travellers = this.travellerCount
  ): AllowanceMeterGroup[] {
    const groups: AllowanceMeterGroup[] = [];

    Object.entries(violations || {}).forEach(([key, violation]) => {
      if (violation?.options && typeof violation.options === 'object') {
        const items = this.expandOptions(violation, travellers);
        if (items.length > 0) {
          groups.push({
            key: violation.category || key,
            label: this.formatCategoryLabel(violation.category || key),
            hint: this.groupHint(violation),
            items: this.sortByFill(items),
          });
        }
        return;
      }
      if (this.isCompleteViolation(violation)) {
        groups.push({
          key: violation.category || key,
          label: this.formatCategoryLabel(violation.category || key),
          items: [this.toMeterItem(violation, travellers)],
        });
      }
    });

    return this.sortGroupsByFill(groups);
  }

  getMeterItems(
    violations: Record<string, ProductRuleViolation>,
    travellers = this.travellerCount
  ): AllowanceMeterItem[] {
    return this.getMeterGroups(violations, travellers).reduce(
      (items, group) => items.concat(group.items),
      [] as AllowanceMeterItem[]
    );
  }

  getProgress(item: AllowanceMeterItem): number {
    if (!item?.allowedLimit) {
      return 0;
    }
    if (item.exceeded && item.usedLimit > 0) {
      return 100;
    }
    return Math.min(
      100,
      Math.round((item.usedLimit / item.allowedLimit) * 100)
    );
  }

  getLimitPercent(item: AllowanceMeterItem): number {
    if (!item?.exceeded || !item.usedLimit) {
      return 0;
    }
    return Math.round((item.allowedLimit / item.usedLimit) * 100);
  }

  getState(item: AllowanceMeterItem | null): AllowanceMeterState {
    if (!item) {
      return 'empty';
    }
    if (item.exceeded) {
      return 'over';
    }
    const progress = this.getProgress(item);
    if (progress <= 0) {
      return 'empty';
    }
    return progress >= 80 ? 'near' : 'ok';
  }

  getOverallState(items: AllowanceMeterItem[]): AllowanceMeterState {
    const states = (items || []).map((item) => this.getState(item));
    if (states.includes('over')) {
      return 'over';
    }
    if (states.includes('near')) {
      return 'near';
    }
    return states.includes('ok') ? 'ok' : 'empty';
  }

  getPrimaryItem(items: AllowanceMeterItem[]): AllowanceMeterItem | null {
    return (
      (items || []).find((item) => item.exceeded) ||
      (items || []).find((item) => item.usedLimit > 0) ||
      (items || [])[0] ||
      null
    );
  }

  /** Only tag a meter once it says something: half full, or over. */
  getTagLabel(item: AllowanceMeterItem): string {
    if (this.getState(item) === 'over') {
      return this.translate.instant('dutyfree.retail_cart.allowance_over_guide');
    }
    if (this.getProgress(item) >= 50) {
      return this.translate.instant(
        'dutyfree.retail_cart.allowance_room_to_spare'
      );
    }
    return '';
  }

  getNote(item: AllowanceMeterItem): string {
    const used = Number(item?.usedLimit) || 0;
    if (used <= 0) {
      return this.translate.instant('dutyfree.retail_cart.allowance_none_yet');
    }
    const allowed = Number(item?.allowedLimit) || 0;
    const remaining = Math.round((allowed - used) * 100) / 100;
    if (remaining < 0) {
      return this.translate.instant('dutyfree.retail_cart.allowance_over_by', {
        amount: Math.abs(remaining),
        unit: this.unitNoun(item),
      });
    }
    return this.translate.instant('dutyfree.retail_cart.allowance_room_for', {
      amount: remaining,
      unit: this.unitNoun(item),
    });
  }

  formatCategoryLabel(category: string): string {
    if (!category) {
      return '';
    }
    return category
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  formatUsedText(item: AllowanceMeterItem): string {
    return `${this.formatNumber(item?.usedLimit)} ${this.shortUnit(
      item?.unit
    )}`.trim();
  }

  formatAllowedText(item: AllowanceMeterItem): string {
    const amount = this.formatNumber(item?.allowedLimit);
    const unit = this.shortUnit(item?.unit);
    if (unit) {
      return `${amount} ${unit}`;
    }
    const noun = String(item?.category || '').toLowerCase();
    return noun ? `${amount} ${noun}` : amount;
  }

  formatStripValue(item: AllowanceMeterItem | null): string {
    if (!item) {
      return '';
    }
    return this.translate.instant('dutyfree.retail_cart.allowance_strip_value', {
      used: this.formatNumber(item.usedLimit),
      allowed: this.formatAllowedText(item),
    });
  }

  /** Fullest first, then anything untouched — keeps used categories on top. */
  private sortByFill(items: AllowanceMeterItem[]): AllowanceMeterItem[] {
    return [...items].sort(
      (a, b) => this.fillRatio(b) - this.fillRatio(a)
    );
  }

  private sortGroupsByFill(groups: AllowanceMeterGroup[]): AllowanceMeterGroup[] {
    return [...groups].sort(
      (a, b) => this.groupFill(b) - this.groupFill(a)
    );
  }

  private groupFill(group: AllowanceMeterGroup): number {
    return (group.items || []).reduce(
      (max, item) => Math.max(max, this.fillRatio(item)),
      0
    );
  }

  private fillRatio(item: AllowanceMeterItem): number {
    const allowed = Number(item?.allowedLimit) || 0;
    const used = Number(item?.usedLimit) || 0;
    if (allowed <= 0) {
      return used > 0 ? Number.MAX_SAFE_INTEGER : 0;
    }
    return used / allowed;
  }

  private expandOptions(
    violation: ProductRuleViolation,
    travellers: number
  ): AllowanceMeterItem[] {
    return Object.entries(violation.options || {})
      .map(([optionKey, option]) => ({ optionKey, option }))
      .filter(({ option }) => this.isCompleteOption(option))
      .map(({ optionKey, option }) =>
        this.scaleForTravellers(
          {
            category: option.category || optionKey,
            allowedLimit: option.allowedLimit,
            usedLimit: option.usedLimit,
            unit: option.unit,
            exceeded: option.exceeded,
          },
          travellers
        )
      );
  }

  /**
   * Backend limits are per adult, so a group allowance is the limit times the
   * traveller count. Guidance only — `exceeded` is re-derived here so the tag,
   * bar colour and note can never disagree.
   */
  private scaleForTravellers(
    item: AllowanceMeterItem,
    travellers: number
  ): AllowanceMeterItem {
    const baseAllowed = Number(item?.allowedLimit) || 0;
    if (!baseAllowed) {
      return item;
    }
    const count = Math.max(1, travellers || 1);
    const allowedLimit = Math.round(baseAllowed * count * 100) / 100;
    const used = Number(item.usedLimit) || 0;
    return {
      ...item,
      allowedLimit,
      exceeded: used > allowedLimit || (count === 1 && !!item.exceeded),
    };
  }

  private toMeterItem(
    violation: ProductRuleViolation,
    travellers: number
  ): AllowanceMeterItem {
    return this.scaleForTravellers(
      {
        category: violation.category,
        allowedLimit: violation.allowedLimit,
        usedLimit: violation.usedLimit,
        unit: violation.unit,
        exceeded: violation.exceeded,
      } as AllowanceMeterItem,
      travellers
    );
  }

  private groupHint(violation: ProductRuleViolation): string | undefined {
    const type = (violation.type || '').toUpperCase();
    if (type === 'OR') {
      return this.translate.instant('dutyfree.retail_cart.pick_one_type_only');
    }
    if (type === 'MULTI') {
      return this.translate.instant(
        'dutyfree.retail_cart.pick_max_or_proportional'
      );
    }
    return undefined;
  }

  private isCompleteOption(option: ProductRuleViolationOption): boolean {
    return (
      option?.allowedLimit != null &&
      option?.unit != null &&
      option?.usedLimit != null
    );
  }

  private isCompleteViolation(violation: ProductRuleViolation): boolean {
    return (
      violation?.allowedLimit != null &&
      violation?.unit != null &&
      violation?.usedLimit != null
    );
  }

  private formatNumber(value: number | undefined): string {
    return String(Number(value) || 0);
  }

  private shortUnit(unit: string | undefined): string {
    const text = String(unit || '').trim();
    if (/^litres?$/i.test(text)) {
      return 'L';
    }
    if (/^count$/i.test(text)) {
      return '';
    }
    return text;
  }

  /** "litres" / "grams" / "cigarettes" — never the raw "count" unit. */
  private unitNoun(item: AllowanceMeterItem): string {
    const unit = String(item?.unit || '').trim();
    if (/^count$/i.test(unit)) {
      return String(item?.category || '').toLowerCase();
    }
    return unit;
  }
}
